import { useState, useEffect, useRef, useMemo, useCallback } from "react"
import { useNavigate, useSearchParams } from "react-router-dom"
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog"
import { UserList } from "./user-list"
import { UserListTable } from "./user-list-table"
import { UserForm } from "./user-form"
import { useOpenUserEditCanvas } from "./use-open-user-edit-canvas"
import { Plus, Mail, Users } from "lucide-react"
import { useUsers, AdminUser } from "@/hooks/use-admin-users"
import { getPageHeader } from "@/constants"
import { PageContainer } from "@/components/ui/page-container"
import { PageHeader } from "@/components/ui/page-header"
import { PageHeaderLeading } from "@/components/navigation/page-header-leading"
import { PageHeaderStack } from "@/components/navigation/page-header-stack"
import {
  PageHeaderActionPrimary,
  PageHeaderActionSecondary,
  PageHeaderActions,
} from "@/components/navigation/page-header-actions"
import { navigateWithThreadPreservation } from "@/utils/thread-navigation"
import { useListFilterBar } from '@/hooks/use-list-filter-bar'
import { ListSortMenu } from '@/components/filters/list-sort-menu'
import { ListViewSwitcher } from '@/components/filters/list-view-switcher'
import {
  ADMIN_USERS_SORT_OPTIONS,
  compareAdminUsers,
  getAdminUsersFilterDefinitions,
  type AdminUsersFilterState,
  type AdminUsersSort,
} from "@/lib/list-filters/admin-users"
import { adminUserDisplayName } from '@/lib/admin-user-display'
import { ADMIN_USERS_VIEW_OPTIONS } from '@/lib/list-view-options'

import { adminPath, type AdminBasePath } from '@/components/admin/use-admin-base-path'
import { SignupPolicySettings } from '@/components/admin/signup-policy-settings'

type AdminUsersViewMode = 'cards' | 'table'

const ADMIN_USERS_VIEW_STORAGE_KEY = 'labs.adminUsers.view'

function readStoredAdminUsersView(): AdminUsersViewMode {
  if (typeof window === 'undefined') return 'cards'
  const value = window.localStorage.getItem(ADMIN_USERS_VIEW_STORAGE_KEY)
  return value === 'table' ? 'table' : 'cards'
}

interface AdminUsersPageProps {
  basePath?: AdminBasePath
}

export function AdminUsersPage({ basePath = '/platform' }: AdminUsersPageProps) {
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const openUserEditCanvas = useOpenUserEditCanvas()
  const filterDefinitions = useMemo(() => getAdminUsersFilterDefinitions(), [])
  const [searchQuery, setSearchQuery] = useState("")
  const [groupFilter, setGroupFilter] = useState("")
  const [sortBy, setSortBy] = useState<AdminUsersSort>("joined_desc")
  const [viewMode, setViewMode] = useState<AdminUsersViewMode>(readStoredAdminUsersView)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)

  useEffect(() => {
    if (typeof window === 'undefined') return
    window.localStorage.setItem(ADMIN_USERS_VIEW_STORAGE_KEY, viewMode)
  }, [viewMode])

  // Track previous users for agent update animation
  const [highlightedUsers, setHighlightedUsers] = useState<Set<string>>(new Set())
  const previousUsersRef = useRef<AdminUser[]>([])

  const { data: users, isLoading, refetch } = useUsers({
    search: searchQuery || undefined,
    group_filter: groupFilter || undefined,
    limit: 50
  })

  const sortedUsers = useMemo(() => {
    if (!users?.length) return users ?? []
    return [...users].sort((a, b) => compareAdminUsers(a, b, sortBy))
  }, [users, sortBy])

  const adminFilters: AdminUsersFilterState = useMemo(
    () => ({
      search: searchQuery,
      group: groupFilter,
    }),
    [searchQuery, groupFilter],
  )

  const onFiltersChange = useCallback((next: AdminUsersFilterState) => {
    setGroupFilter(next.group)
  }, [])

  const clearFilters = useCallback(() => {
    setSearchQuery("")
    setGroupFilter("")
    setSortBy("joined_desc")
  }, [])

  const toolbar = useMemo(
    () => ({
      viewSwitcher: (
        <ListViewSwitcher
          value={viewMode}
          onValueChange={setViewMode}
          options={ADMIN_USERS_VIEW_OPTIONS}
          ariaLabel="User list view"
        />
      ),
      sortMenu: (
        <ListSortMenu
          options={ADMIN_USERS_SORT_OPTIONS}
          value={sortBy}
          onValueChange={(value) => setSortBy(value as AdminUsersSort)}
          ariaLabel={viewMode === 'table' ? 'Sort users (use column headers in table view)' : 'Sort users'}
          disabled={viewMode === 'table'}
        />
      ),
    }),
    [sortBy, viewMode],
  )

  const { filterBar } = useListFilterBar({
    listId: 'adminUsers',
    definitions: filterDefinitions,
    filters: adminFilters,
    onFiltersChange,
    ctx: {},
    onClearAll: clearFilters,
    hasActiveExtras: sortBy !== 'joined_desc',
    toolbar,
    search: {
      searchId: 'admin-users-search',
      searchPlaceholder: 'Search users by email or name…',
      searchValue: searchQuery,
      onSearchChange: setSearchQuery,
    },
  })

  // Detect agent updates and highlight changed users
  useEffect(() => {
    if (users && previousUsersRef.current.length > 0) {
      const changedIds = new Set<string>()
      
      users.forEach((user) => {
        const prev = previousUsersRef.current.find((u) => u.id === user.id)
        if (!prev) {
          changedIds.add(user.id)
        } else if (JSON.stringify(prev) !== JSON.stringify(user)) {
          changedIds.add(user.id)
        }
      })
      
      if (changedIds.size > 0) {
        setHighlightedUsers(changedIds)
        setTimeout(() => {
          setHighlightedUsers(new Set())
        }, 3000)
      }
    }
    
    if (users) {
      previousUsersRef.current = users
    }
  }, [users])

  const handleUserClick = (userId: string) => {
    navigateWithThreadPreservation(navigate, adminPath(basePath, `users/${userId}`), searchParams)
  }

  const handleEditUser = (userId: string, userLabel?: string) => {
    openUserEditCanvas(userId, userLabel, { platformMode: basePath === '/platform' })
  }

  const handleCreateSuccess = () => {
    setCreateDialogOpen(false)
    refetch()
  }

  return (
    <PageContainer>
      <PageHeaderStack>
        <PageHeader
          leading={<PageHeaderLeading />}
          {...getPageHeader(`${basePath}/users`, "User management", "Manage user accounts, permissions, and access controls")}
          icon={<Users className="h-5 w-5" />}
          actions={
            <PageHeaderActions
              secondary={
                <PageHeaderActionSecondary onClick={() => navigate(adminPath(basePath, 'invite'))}>
                  <Mail className="h-4 w-4" aria-hidden />
                  Invite User
                </PageHeaderActionSecondary>
              }
              primary={
                <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
                  <DialogTrigger asChild>
                    <PageHeaderActionPrimary>
                      <Plus className="h-4 w-4" aria-hidden />
                      Create User
                    </PageHeaderActionPrimary>
                  </DialogTrigger>
                  <DialogContent className="max-w-2xl max-h-[90dvh] overflow-y-auto">
                    <DialogHeader>
                      <DialogTitle>Create new user</DialogTitle>
                      <DialogDescription>
                        Create a new user account with appropriate permissions and groups.
                      </DialogDescription>
                    </DialogHeader>
                    <UserForm
                      platformMode={basePath === '/platform'}
                      onSuccess={handleCreateSuccess}
                      onCancel={() => setCreateDialogOpen(false)}
                    />
                  </DialogContent>
                </Dialog>
              }
            />
          }
        />

        {filterBar}
      </PageHeaderStack>

      <SignupPolicySettings />

      {viewMode === 'cards' ? (
          <UserList
            users={sortedUsers}
            isLoading={isLoading}
            onUserClick={handleUserClick}
            onEditUser={(userId, user) => handleEditUser(userId, user ? adminUserDisplayName(user) : undefined)}
            highlightedUserIds={highlightedUsers}
          />
        ) : (
          <UserListTable
            users={sortedUsers}
            isLoading={isLoading}
            sortBy={sortBy}
            onSortChange={setSortBy}
            onUserClick={handleUserClick}
            onEditUser={(userId, user) => handleEditUser(userId, user ? adminUserDisplayName(user) : undefined)}
            highlightedUserIds={highlightedUsers}
          />
        )}
    </PageContainer>
  )
}
